import { Box, ThemeProvider } from '@mui/material';
import { AnimatePresence, motion } from 'motion/react';
import { FunctionComponent, PropsWithChildren, useState } from 'react';
import { GameTheme } from '../../config/GameTheme';
import { adaptTransition, AsyncAnimationProps, waitForAnimation } from '../../util/Animation';
import { muiStyleSheet } from '../../util/Styles';
import { FullScreen, Widescreen } from '../layout/ScreenLayout';
import { ScreenTitle } from '../ScreenTitle';
import { GameLogo } from './GameLogo';

type Props = PropsWithChildren<{
	title: string;
}>;

export const GameLogoScreenLayout: FunctionComponent<Props> = (props) => {
	const [showTransition, setShowTransition] = useState(true);

	async function handleTransitionComplete() {
		await waitForAnimation({ seconds: .5 });
		setShowTransition(false);
	}

	return <ThemeProvider theme={GameTheme}>
		<ScreenTitle title={props.title} />
		<FullScreen sx={styles.container}>
			<GameLogo show={true} sx={styles.logo} />
			<Widescreen>
				{showTransition || props.children}
			</Widescreen>
		</FullScreen>
		<AnimatePresence mode="wait">
			<Transition show={showTransition} onComplete={handleTransitionComplete} />
		</AnimatePresence>
	</ThemeProvider>;
};

/** Simple fade to background color */
const Transition: FunctionComponent<AsyncAnimationProps> = (props) => {
	return props.show && <motion.div
		initial={{ opacity: 1 }}
		animate={{ opacity: 0 }}
		transition={adaptTransition({ delay: 1, duration: 1.5, ease: 'easeInOut' })}
		onAnimationComplete={props.onComplete}
	>
		<FullScreen>
			<Box sx={styles.transitionBackground} />
		</FullScreen>
	</motion.div>;
};

const styles = muiStyleSheet({
	container: theme => ({
		backgroundColor: theme.palette.game.logoBackground,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	}),
	logo: {
		position: 'absolute',
		top: 0, bottom: 0,
		left: 0, right: 0,
	},
	transitionBackground: theme => ({
		height: '100%',
		width: '100%',
		backgroundColor: theme.palette.game.logoBackground,
	}),
});
